import React from 'react';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { isHolidayDate, isWeekendDate } from 'common-react';
import { useTranslation } from 'react-i18next';
import { MultiButton } from '../form/MultiButton/MultiButton';
import { toDropdownOptions } from '../form/Dropdown/tools/Dropdown.utils';
import { Typography } from '../Typography/Typography';
import { DatePicker } from '../DatePicker/DatePicker';
import useStyles from './ChartDateRangeControls.style';

type RangeChangeEvent = React.ChangeEvent<{ name?: string; value: unknown }>;

export type ChartDateRangeControlsProps = {
  rangeLabel?: string;
  rangeKeys: string[];
  selectedRangeKey?: string;
  onRangeChange: (event: RangeChangeEvent) => void;
  radioGroupName: string;
  fromDateLabel?: string;
  fromDateValue: string;
  fromDateErrorMessage?: string;
  onFromDateChange: (date: MaterialUiPickersDate) => void;
  toDateLabel?: string;
  toDateValue: string;
  toDateErrorMessage?: string;
  onToDateChange: (date: MaterialUiPickersDate) => void;
  shouldDisableDate?: (date: MaterialUiPickersDate) => boolean;
  maxDate?: string;
};

export const ChartDateRangeControls: React.FC<ChartDateRangeControlsProps> = ({
  rangeLabel,
  rangeKeys,
  selectedRangeKey,
  onRangeChange,
  radioGroupName,
  fromDateLabel,
  fromDateValue,
  fromDateErrorMessage,
  onFromDateChange,
  toDateLabel,
  toDateValue,
  toDateErrorMessage,
  onToDateChange,
  shouldDisableDate,
  maxDate,
}) => {
  const [t] = useTranslation('components');
  const c = useStyles();
  const disableDate = shouldDisableDate ?? ((date: MaterialUiPickersDate): boolean => {

    if (date == null) {
      return true;
    }

    if (isWeekendDate(date) || isHolidayDate(date)) {
      return true;
    }

    if (!maxDate) {
      return false;
    }

    return new Date(maxDate) < date;
  });

  return (
    <>
      <div>
        <Typography fontWeight="bold" className={c.rangeRadiosLabel}>
          {rangeLabel ?? t('fundChart.choose-time-range')}
        </Typography>
        <MultiButton
            options={toDropdownOptions(rangeKeys)}
            getCheckedValue={(value: string) => selectedRangeKey === value}
            onChange={onRangeChange}
            radioGroupName={radioGroupName}
        />
      </div>
      <div className={c.datepickerBoxWrapper}>
        <div className={c.datepickerBox}>
          <DatePicker
              className={c.datePicker}
              classNameErrorMessage={c.datePickerErrorMessage}
              label={fromDateLabel ?? t('fundChart.from-date')}
              validateOnChange={false}
              value={fromDateValue}
              onChange={onFromDateChange}
              errorMessage={fromDateErrorMessage}
              shouldDisableDate={disableDate}
          />
          <DatePicker
              className={c.datePicker}
              classNameErrorMessage={c.datePickerErrorMessage}
              label={toDateLabel ?? t('fundChart.to-date')}
              validateOnChange={false}
              value={toDateValue}
              onChange={onToDateChange}
              errorMessage={toDateErrorMessage}
              shouldDisableDate={disableDate}
          />
        </div>
      </div>
    </>
  );
};
